import { useEffect } from "react";
import { useBookingFlow } from "../../context/use-booking-flow";
import { Loader } from "lucide-react";
import { useBooking } from "../../hooks/use-booking";
import {
  DefaultPoContractFlowStep,
  UpsellPoContractFlowStep,
} from "../../context/use-booking-flow/use-booking-flow.types";
import { useCreatePoContract } from "../../../../../hooks/useCreatePrivateOfficeContract";
import { extractErrorMessage } from "@/utils/extract-error-message";

export const BookingLoading = () => {
  const {
    bookingData,
    upsellData,
    flowType,
    nextStep,
    setBookingFlowStatus,
    setErrorMessages,
    processingStatus,
    setProcessingStatus,
    selectedCompany,
    selectedPackage,
    currentStep,
    poContractCreateData,
  } = useBookingFlow();
  const { handleCreateBooking, handleCreateUpsell } = useBooking();

  const { handleCreatePoContract } = useCreatePoContract();

  const handleError = (
    error: unknown,
    type: "booking" | "upsell" | "poContract",
  ) => {
    console.error(`Error creating ${type}:`, error);
    const errorMessage = extractErrorMessage(error, `Erro ao criar ${type}`);
    setBookingFlowStatus({ [type]: "error" });
    setErrorMessages({ [type]: errorMessage });
  };

  const createBooking = async () => {
    try {
      const bookingSuccess =
        bookingData && (await handleCreateBooking(bookingData));
      if (bookingSuccess) {
        setBookingFlowStatus({ booking: "success" });
        return true;
      }
      return;
    } catch (error) {
      handleError(error, "booking");
      return false;
    }
  };

  const createPoContract = async () => {
    try {
      const poContractSuccess =
        poContractCreateData &&
        (await handleCreatePoContract(poContractCreateData));

      if (poContractSuccess) {
        setBookingFlowStatus({ poContract: "success" });
        return true;
      }

      return false;
    } catch (error) {
      setBookingFlowStatus({ poContract: "error" });
      const errorMessage = extractErrorMessage(error, "Erro ao criar contrato");
      setErrorMessages({ ["poContract"]: errorMessage });
    }
  };

  const createUpsell = async () => {
    try {
      if (!selectedCompany || !selectedPackage || !bookingData) {
        handleError(
          new Error("Dados de empresa, pacote ou booking não carregados"),
          "upsell",
        );
        return false;
      }

      const cyclesToUpdate =
        upsellData?.simulatedUpsellData.updatedCycles?.map((cycle) => {
          const additionalPayments = upsellData.simulatedUpsellData.newPayments
            .filter((payment) => payment.cycleId === cycle.cycleId)
            .map((payment) => ({
              cycleId: payment.cycleId,
              amount: payment.amount,
              capacity: payment.capacity,
              paymentDate: payment.paymentDate,
            }));

          return {
            id: cycle.cycleId,
            creditAmount: cycle.newCapacity,
            additionalPayments,
          };
        }) ?? [];

      const upsellSuccess = await handleCreateUpsell({
        companyId: selectedCompany.id,
        packageId: selectedPackage.id,
        beginDate: bookingData.start_date,
        cyclesToUpdate,
      });

      if (!upsellSuccess.success) {
        handleError(new Error(upsellSuccess.data.message as string), "upsell");
        return false;
      }

      setBookingFlowStatus({ upsell: "success" });
      return true;
    } catch (error) {
      handleError(error, "upsell");
      return false;
    }
  };

  useEffect(() => {
    const createBookingFlow = async () => {
      try {
        if (!bookingData) {
          throw new Error("Dados do booking são obrigatórios");
        }

        if (processingStatus.isProcessing) return;

        if (
          currentStep === DefaultPoContractFlowStep.LOADING ||
          currentStep === UpsellPoContractFlowStep.LOADING
        ) {
          setProcessingStatus({
            isProcessing: true,
            canNavigateAway: false,
            currentOperation: "po-contract",
          });

          const poContractSuccess = await createPoContract();
          if (!poContractSuccess) {
            setBookingFlowStatus({ poContract: "error" });
            return;
          }
        } else {
          setProcessingStatus({
            isProcessing: true,
            canNavigateAway: false,
            currentOperation: "booking",
          });

          const bookingSuccess = await createBooking();
          if (!bookingSuccess) {
            setBookingFlowStatus({ booking: "error" });
            return;
          }
        }

        if (flowType === "upsell" && upsellData) {
          setProcessingStatus({ currentOperation: "upsell" });
          const upsellSuccess = await createUpsell();
          if (!upsellSuccess) {
            setBookingFlowStatus({ upsell: "error" });
          }
        }
      } finally {
        setProcessingStatus({
          isProcessing: false,
          canNavigateAway: true,
          currentOperation: null,
        });
        nextStep();
      }
    };

    createBookingFlow();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px]">
      <Loader className="h-12 w-12 animate-spin text-zinc-950" />
      <h2 className="text-xl font-semibold mt-4">
        {processingStatus.currentOperation === "booking" &&
          "Criando a sua reserva..."}
        {processingStatus.currentOperation === "upsell" &&
          "Realizando upsell de pacotes..."}
        {processingStatus.currentOperation === "po-contract" &&
          "Criando o contrato..."}
      </h2>
      <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight mt-4">
        Este processo pode levar alguns instantes. <br />
        Por favor, mantenha esta página aberta e não desligue seu computador.
      </div>
    </div>
  );
};
